import { MessageType } from '../types/messageType';
import { SendGift } from './giftMessage';
import { MessageUser } from './messageUser';
import { SendMessage, UserJoinMessage } from './sendMessage';

export enum LiveMessageType {
  Join,
  Normal,
  Piece,
  Gift,
  Share,
  Follow,
  Nice,
}

export class LiveComment {
  timestamp?: number;
  comment?: string;
  commentUser?: MessageUser;
  commentConfig?: MessageType;
  liveId?: string;
}

const convertToSendMessage = (message: any, type: MessageType): LiveComment => {
  let liveComment: LiveComment = {};
  // User join
  if (type === MessageType.JOIN_USER) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value?.user,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // User leave
  if (type === MessageType.LEAVE_USER) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }

  // Text Comment
  if (type === MessageType.SEND_MESSAGE) {
    liveComment = {
      timestamp: Date.now(),
      comment: message.value?.message,
      commentUser: message.value?.user,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }

  // Send Gift
  if (type === MessageType.SEND_GIFT) {
    const giftMessage = message as SendGift;
    liveComment = {
      timestamp: Date.now(),
      comment: JSON.stringify(giftMessage.value),
      commentUser: giftMessage.value.sender,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }

  // Gift top
  if (type === MessageType.GIFT_TOP) {
    liveComment = {
      timestamp: Date.now(),
      comment: message.value,
      commentUser: undefined,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }

  // Send nice
  if (type === MessageType.SEND_NICE) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value?.user,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }

  // Follow liver
  if (type === MessageType.FOLLOW_USER) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // start accepting pair
  if (type === MessageType.START_ACCEPTING_PAIR) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // end accepting pair
  if (type === MessageType.END_ACCEPTING_PAIR) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // start request pair
  if (type === MessageType.START_PAIR_REQUEST) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // end request pair
  if (type === MessageType.END_PAIR_REQUEST) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // start pair
  if (type === MessageType.START_PAIR) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // end pair
  if (type === MessageType.END_PAIR) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // request live match
  if (type === MessageType.REQUEST_LIVE_MATCH) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value.liveMatch,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }
  // start live match
  if (type === MessageType.START_LIVE_MATCH) {
    liveComment = {
      timestamp: Date.now(),
      commentUser: message.value.liveMatch,
      commentConfig: type,
      liveId: message.live,
    };
    return liveComment;
  }

  return liveComment;
};

/**
 * Convert Rtm message to LiveComment
 * @param text: content of rtm message
 * @returns LiveComment object
 */
export const convertRtmMessage = (text: string): LiveComment => {
  const liveComment: LiveComment = {};
  // user comment
  if (text.indexOf(MessageType.SEND_MESSAGE) > -1) {
    const message = JSON.parse(JSON.parse(text).text) as SendMessage;
    return convertToSendMessage(message, MessageType.SEND_MESSAGE);
  }

  // user joined
  if (text.indexOf(MessageType.JOIN_USER) > -1) {
    const message = JSON.parse(JSON.parse(text).text) as UserJoinMessage;
    return convertToSendMessage(message, MessageType.JOIN_USER);
  }

  // user left
  if (text.indexOf(MessageType.LEAVE_USER) > -1) {
    const message = JSON.parse(JSON.parse(text).text) as SendMessage;
    return convertToSendMessage(message, MessageType.LEAVE_USER);
  }

  // user send gift
  if (text.indexOf(MessageType.SEND_GIFT) > -1) {
    const gifMessage = JSON.parse(JSON.parse(text).text) as SendGift;
    return convertToSendMessage(gifMessage, MessageType.SEND_GIFT);
  }

  // gift top changed
  if (text.indexOf(MessageType.GIFT_TOP) > -1) {
    const gifMessage = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(gifMessage, MessageType.GIFT_TOP);
  }

  // audience send nice
  if (text.indexOf(MessageType.SEND_NICE) > -1) {
    const niceMessage = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(niceMessage, MessageType.SEND_NICE);
  }

  // follow liver message
  if (text.indexOf(MessageType.FOLLOW_USER) > -1) {
    const followMessage = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(followMessage, MessageType.FOLLOW_USER);
  }
  // start accepting pair
  if (text.indexOf(MessageType.START_ACCEPTING_PAIR) > -1) {
    const startAcceptPair = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(
      startAcceptPair,
      MessageType.START_ACCEPTING_PAIR,
    );
  }
  // end accepting pair
  if (text.indexOf(MessageType.END_ACCEPTING_PAIR) > -1) {
    const endAcceptPair = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(endAcceptPair, MessageType.END_ACCEPTING_PAIR);
  }
  // start pair request
  if (text.indexOf(MessageType.START_PAIR_REQUEST) > -1) {
    const startPairRequest = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(
      startPairRequest,
      MessageType.START_PAIR_REQUEST,
    );
  }
  // end pair request
  if (text.indexOf(MessageType.END_PAIR_REQUEST) > -1) {
    const endPairRequest = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(endPairRequest, MessageType.END_PAIR_REQUEST);
  }
  // start pair
  if (text.indexOf(MessageType.START_PAIR) > -1) {
    const startPair = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(startPair, MessageType.START_PAIR);
  }
  // end pair
  if (text.indexOf(MessageType.END_PAIR) > -1) {
    const endPair = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(endPair, MessageType.END_PAIR);
  }
  // request live match
  if (text.indexOf(MessageType.REQUEST_LIVE_MATCH) > -1) {
    const endPair = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(endPair, MessageType.REQUEST_LIVE_MATCH);
  }
  // start live match
  if (text.indexOf(MessageType.START_LIVE_MATCH) > -1) {
    const endPair = JSON.parse(JSON.parse(text).text);
    return convertToSendMessage(endPair, MessageType.START_LIVE_MATCH);
  }

  return liveComment;
};
